import { Capacitor, registerPlugin } from '@capacitor/core';
import type { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { api } from './api';
import type { Ride } from './types';

/**
 * Standortverfolgung für den Fahrer.
 *
 * Zwei Wege, eine Schnittstelle:
 *  - Im Browser `watchPosition` plus Wake Lock. Das hält nur, solange das
 *    Display an ist - deshalb gibt es die Android-App.
 *  - In der Android-App der Foreground-Service des Background-Geolocation-
 *    Plugins. Der läuft weiter, wenn der Bildschirm aus ist.
 */

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');

/** Mindestabstand zwischen zwei Uploads - schont Akku und Datenvolumen. */
const MIN_UPLOAD_INTERVAL_MS = 20_000;
/** Auch ohne Bewegung mindestens einmal pro Minute ein Lebenszeichen senden. */
const HEARTBEAT_INTERVAL_MS = 60_000;

export interface Fix {
  lat: number;
  lng: number;
  accuracy: number | null;
  at: number;
}

export interface TrackerCallbacks {
  onRide?: (ride: Ride) => void;
  onFix?: (fix: Fix) => void;
  onError?: (message: string) => void;
}

export interface Tracker {
  stop: () => Promise<void>;
  /** true, wenn im Hintergrund weiter getrackt wird (Android-App). */
  readonly background: boolean;
}

export const isNative = (): boolean => Capacitor.isNativePlatform();

/** Hält das Display an, solange die Web-Variante läuft. */
async function acquireWakeLock(): Promise<WakeLockSentinel | null> {
  try {
    if (!('wakeLock' in navigator)) return null;
    return await navigator.wakeLock.request('screen');
  } catch {
    return null; // Kein Wake Lock ist unschön, aber kein Fehler.
  }
}

export async function startTracking(
  rideId: string,
  token: string,
  callbacks: TrackerCallbacks = {},
): Promise<Tracker> {
  let stopped = false;
  let lastUploadAt = 0;
  let pending: Fix | null = null;
  let inFlight = false;

  async function upload(fix: Fix): Promise<void> {
    if (stopped || inFlight) {
      pending = fix;
      return;
    }
    inFlight = true;
    try {
      const { ride } = await api.sendLocation(rideId, token, {
        lat: fix.lat,
        lng: fix.lng,
        accuracy: fix.accuracy,
        at: fix.at,
      });
      lastUploadAt = Date.now();
      callbacks.onRide?.(ride);
    } catch (error) {
      // Funkloch: der nächste Fix versucht es einfach erneut.
      callbacks.onError?.(error instanceof Error ? error.message : 'Position konnte nicht gesendet werden.');
    } finally {
      inFlight = false;
      const queued = pending;
      pending = null;
      if (queued && !stopped) void upload(queued);
    }
  }

  function handleFix(fix: Fix): void {
    if (stopped) return;
    callbacks.onFix?.(fix);
    if (Date.now() - lastUploadAt < MIN_UPLOAD_INTERVAL_MS) {
      pending = fix; // Der Heartbeat-Timer schiebt ihn später nach.
      return;
    }
    void upload(fix);
  }

  // Auch im Stand ein Lebenszeichen senden, damit der Mitfahrer sieht,
  // dass die App noch läuft (Stau vs. abgestürzt).
  const heartbeat = window.setInterval(() => {
    if (stopped) return;
    const queued = pending;
    if (queued) {
      pending = null;
      void upload(queued);
      return;
    }
    if (Date.now() - lastUploadAt >= HEARTBEAT_INTERVAL_MS) {
      api.heartbeat(rideId, token)
        .then(({ ride }) => callbacks.onRide?.(ride))
        .catch(() => { /* stilles Lebenszeichen, Fehler sind hier egal */ });
    }
  }, HEARTBEAT_INTERVAL_MS / 2);

  if (isNative()) {
    const watcherId = await BackgroundGeolocation.addWatcher(
      {
        backgroundTitle: 'R2F verfolgt deine Fahrt',
        backgroundMessage: 'Antippen zum Öffnen. Wegwischen beendet die Standortfreigabe.',
        requestPermissions: true,
        stale: false,
        distanceFilter: 25,
      },
      (location, error) => {
        if (error) {
          if (error.code === 'NOT_AUTHORIZED') {
            callbacks.onError?.(
              'Die Standortberechtigung fehlt. Bitte in den Android-Einstellungen ' +
              'auf "Immer zulassen" stellen.',
            );
          } else {
            callbacks.onError?.(error.message ?? 'Standort nicht verfügbar.');
          }
          return;
        }
        if (!location) return;
        handleFix({
          lat: location.latitude,
          lng: location.longitude,
          accuracy: location.accuracy ?? null,
          at: location.time ?? Date.now(),
        });
      },
    );

    return {
      background: true,
      stop: async () => {
        stopped = true;
        window.clearInterval(heartbeat);
        await BackgroundGeolocation.removeWatcher({ id: watcherId });
      },
    };
  }

  // --- Browser-Variante ---
  if (!('geolocation' in navigator)) {
    window.clearInterval(heartbeat);
    throw new Error('Dieses Gerät kann keinen Standort bestimmen.');
  }

  const wakeLock = await acquireWakeLock();

  const watchId = navigator.geolocation.watchPosition(
    (position) =>
      handleFix({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy ?? null,
        at: position.timestamp,
      }),
    (error) => {
      const messages: Record<number, string> = {
        1: 'Standortfreigabe abgelehnt. Bitte für diese Seite erlauben.',
        2: 'Standort gerade nicht verfügbar.',
        3: 'Standortbestimmung hat zu lange gedauert.',
      };
      callbacks.onError?.(messages[error.code] ?? error.message);
    },
    { enableHighAccuracy: true, maximumAge: 5000, timeout: 20_000 },
  );

  return {
    background: false,
    stop: async () => {
      stopped = true;
      window.clearInterval(heartbeat);
      navigator.geolocation.clearWatch(watchId);
      await wakeLock?.release().catch(() => { /* schon freigegeben */ });
    },
  };
}

/** Einmalige Standortabfrage, z. B. für "Mein Standort" beim Setzen eines Punktes. */
export function getCurrentPosition(): Promise<Fix> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Dieses Gerät kann keinen Standort bestimmen.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy ?? null,
          at: position.timestamp,
        }),
      (error) =>
        reject(
          new Error(
            error.code === 1
              ? 'Standortfreigabe abgelehnt. Bitte für diese Seite erlauben.'
              : 'Standort konnte nicht ermittelt werden.',
          ),
        ),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 10_000 },
    );
  });
}
